import { SheetShapeDeepFragment } from '../../gql/graphql';
import {
  GeometricResult,
  MultiPolygon,
  MultiPolygon2,
  Plane,
  Point,
  Point2,
  Triangle,
} from './geometric-types';
import { SlopedInsulationCrossSection } from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';

// Gometric properties and algorithms should have no overlapping names
// Because we currently have no other way to separate them than string name...
export enum MeshAlgorithmType {
  VERTICAL_COMPONENT = 'Vertical component',
  BOUNDING_BOX_LENGTH = 'Bounding box length',
  BOUNDING_BOX_HORIZONTAL_LENGTH = 'Bounding box horizontal length',
  BOUNDING_BOX_HORIZONTAL_WIDTH = 'Bounding box horizontal width',
  LARGEST_FLAT_SIDE_AREA = 'Largest flat surface',
  CONVEX_LARGEST_FLAT_SIDE_AREA = 'Gross side area include depth',
  CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY = 'Gross side area ignore depth',
  LARGEST_SURFACE_AREA = 'Largest surface area',
  LARGEST_SURFACE_AREA_WITHOUT_OPENINGS = 'Largest surface area without openings',
  TOP_SURFACE_AREA = 'Top surface area',
  BOTTOM_SURFACE_AREA = 'Bottom surface area',
  TOTAL_MESH_AREA = 'Total surface area',
  TETRAHEDRON_VOLUME = 'Summed tetrahedron volume',
  MESH_PERIMETER = 'Mesh perimeter',
  MESH_LOCATION_MIN_X = 'Mesh location min x',
  MESH_LOCATION_MIN_Y = 'Mesh location min y',
  MESH_LOCATION_MIN_Z = 'Mesh location min z',
  MESH_LOCATION_MAX_X = 'Mesh location max x',
  MESH_LOCATION_MAX_Y = 'Mesh location max y',
  MESH_LOCATION_MAX_Z = 'Mesh location max z',
}

export enum MultiPolygonAlgorithmType {
  POLYGON_VERTICAL_COMPONENT = 'Shapes vertical component',
  POLYGON_PERIMETER = 'Shapes perimeter',
  POLYGON_AREA = 'Shapes largest side area',
  POLYGON_GROSS_SIDE_AREA = 'Shapes gross side area',
  POLYGON_BOUNDING_BOX_WIDTH = 'Shapes bounding box width',
  POLYGON_BOUNDING_BOX_LENGTH = 'Shapes bounding box length',
  POLYGON_LOCATION_MIN_X = 'Shapes location min x',
  POLYGON_LOCATION_MIN_Y = 'Shapes location min y',
  POLYGON_LOCATION_MIN_Z = 'Shapes location min z',
  POLYGON_LOCATION_MAX_X = 'Shapes location max x',
  POLYGON_LOCATION_MAX_Y = 'Shapes location max y',
  POLYGON_LOCATION_MAX_Z = 'Shapes location max z',
}

export enum ExtrudedPolygonAlgorithmType {
  EXTRUDED_POLYGON_PERIMETER = 'Extruded shape base perimeter',
  EXTRUDED_POLYGON_AREA = 'Extruded shape base area',
  EXTRUDED_POLYGON_THICKNESS = 'Extruded shape thickness',
  EXTRUDED_POLYGON_VOLUME = 'Extruded shape volume',
  EXTRUDED_POLYGON_SIDE_AREA = 'Extruded shape side area',
  EXTRUDED_POLYGON_BOUNDING_BOX_WIDTH = 'Extruded shape bounding box width',
  EXTRUDED_POLYGON_BOUNDING_BOX_LENGTH = 'Extruded shape bounding box length',
  EXTRUDED_POLYGON_LOCATION_MIN_X = 'Extruded shape location min x',
  EXTRUDED_POLYGON_LOCATION_MIN_Y = 'Extruded shape location min y',
  EXTRUDED_POLYGON_LOCATION_MIN_Z = 'Extruded shape location min z',
  EXTRUDED_POLYGON_LOCATION_MAX_X = 'Extruded shape location max x',
  EXTRUDED_POLYGON_LOCATION_MAX_Y = 'Extruded shape location max y',
  EXTRUDED_POLYGON_LOCATION_MAX_Z = 'Extruded shape location max z',
}

export enum PolylineAlgorithmType {
  LINE_LENGTH = 'Line length',
  LINE_LOCATION_MIN_X = 'Line location min x',
  LINE_LOCATION_MIN_Y = 'Line location min y',
  LINE_LOCATION_MIN_Z = 'Line location min z',
  LINE_LOCATION_MAX_X = 'Line location max x',
  LINE_LOCATION_MAX_Y = 'Line location max y',
  LINE_LOCATION_MAX_Z = 'Line location max z',
}

export enum PointAlgorithmType {
  POINT_LOCATION_X = 'Point location x',
  POINT_LOCATION_Y = 'Point location y',
  POINT_LOCATION_Z = 'Point location z',
}

export enum MultiPolygon2AlgorithmType {
  POLYGON2_PERIMETER = 'Sheet shapes perimeter',
  POLYGON2_INTERNAL_PERIMETER = 'Sheet shapes internal perimeter',
  POLYGON2_NUMBER_OF_EXTERNAL_CORNERS = 'Sheet shapes number of external corners',
  POLYGON2_NUMBER_OF_CONVEX_CORNERS = 'Sheet shapes number of convex corners',
  POLYGON2_NUMBER_OF_REFLEX_CORNERS = 'Sheet shapes number of reflex corners',
  POLYGON2_AREA = 'Sheet shapes largest side area',
  POLYGON2_GROSS_SIDE_AREA = 'Sheet shapes gross side area',
  POLYGON2_FIRE_SECTIONING = 'Sheet shape inner area parameterized',
  POLYGON2_CORNER_AREA = 'Sheet shape corner area parameterized',
  POLYGON2_RAND_AREA = 'Sheet shape rand area parameterized',
  POLYGON2_BOUNDING_BOX_WIDTH = 'Sheet shapes bounding box width',
  POLYGON2_BOUNDING_BOX_LENGTH = 'Sheet shapes bounding box length',
  POLYGON2_LOCATION_MIN_X = 'Sheet shapes location min x',
  POLYGON2_LOCATION_MIN_Y = 'Sheet shapes location min y',
  POLYGON2_LOCATION_MAX_X = 'Sheet shapes location max x',
  POLYGON2_LOCATION_MAX_Y = 'Sheet shapes location max y',
}

export enum Polyline2AlgorithmType {
  LINE2_LENGTH = 'Sheet line length',
  LINE2_CROSS_SECTION_BASELINE_LENGTH = 'Sheet line cross section baseline length',
  LINE2_CROSS_SECTION_DIAGONAL_LENGTH = 'Sheet line cross section diagonal length',
  LINE2_LOCATION_MIN_X = 'Sheet line location min x',
  LINE2_LOCATION_MIN_Y = 'Sheet line location min y',
  LINE2_LOCATION_MAX_X = 'Sheet line location max x',
  LINE2_LOCATION_MAX_Y = 'Sheet line location max y',
}

export enum Point2AlgorithmType {
  POINT2_LOCATION_X = 'Sheet point location x',
  POINT2_LOCATION_Y = 'Sheet point location y',
}

export type GeometricAlgorithmType =
  | MeshAlgorithmType
  | MultiPolygonAlgorithmType
  | ExtrudedPolygonAlgorithmType
  | PolylineAlgorithmType
  | PointAlgorithmType
  | MultiPolygon2AlgorithmType
  | Polyline2AlgorithmType
  | Point2AlgorithmType;

export const geometricAlgorithmTypes = [
  ...Object.values(MeshAlgorithmType),
  ...Object.values(PolylineAlgorithmType),
  ...Object.values(MultiPolygonAlgorithmType),
  ...Object.values(ExtrudedPolygonAlgorithmType),
  ...Object.values(MultiPolygon2AlgorithmType),
  ...Object.values(Polyline2AlgorithmType),
  ...Object.values(PointAlgorithmType),
  ...Object.values(Point2AlgorithmType),
];

export const isGeometricAlgorithmType = (
  propertyName: string
): propertyName is GeometricAlgorithmType => {
  return geometricAlgorithmTypes.includes(
    propertyName as GeometricAlgorithmType
  );
};

export const isMeshAlgorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is MeshAlgorithmType =>
  Object.values(MeshAlgorithmType).includes(algorithmType as MeshAlgorithmType);

export const isPolygonAlgorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is MultiPolygonAlgorithmType =>
  Object.values(MultiPolygonAlgorithmType).includes(
    algorithmType as MultiPolygonAlgorithmType
  );

export const isExtrudedPolygonAlgorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is ExtrudedPolygonAlgorithmType =>
  Object.values(ExtrudedPolygonAlgorithmType).includes(
    algorithmType as ExtrudedPolygonAlgorithmType
  );

export const isPolylineAlgorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is PolylineAlgorithmType =>
  Object.values(PolylineAlgorithmType).includes(
    algorithmType as PolylineAlgorithmType
  );

export const isPointAlgorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is PointAlgorithmType =>
  Object.values(PointAlgorithmType).includes(
    algorithmType as PointAlgorithmType
  );

export const isPolygon2Algorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is MultiPolygon2AlgorithmType =>
  Object.values(MultiPolygon2AlgorithmType).includes(
    algorithmType as MultiPolygon2AlgorithmType
  );

export const isPolyline2Algorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is Polyline2AlgorithmType =>
  Object.values(Polyline2AlgorithmType).includes(
    algorithmType as Polyline2AlgorithmType
  );

export const isPoint2Algorithm = (
  algorithmType: GeometricAlgorithmType
): algorithmType is Point2AlgorithmType =>
  Object.values(Point2AlgorithmType).includes(
    algorithmType as Point2AlgorithmType
  );

// An algorithm that operates on a mesh
export type MeshAlgorithm<T extends GeometricResult = GeometricResult> = (
  triangles: Triangle[]
) => T;

// An algorithm that operates on a set of points
export type PointCloudAlgorithm<T extends GeometricResult = GeometricResult> = (
  points: Point[]
) => T;

// An algorithm that operates on a polygon
export type MultiPolygonAlgorithm<T extends GeometricResult = GeometricResult> =
  (polygon: MultiPolygon, plane: Plane) => T;

// An algorithm that operates on an extruded polygon
export type ExtrudedPolygonAlgorithm<
  T extends GeometricResult = GeometricResult
> = (polygon: MultiPolygon, plane: Plane, thickness: number) => T;

// An algorithm that operates on a polyline
export type PolylineAlgorithm<T extends GeometricResult = GeometricResult> = (
  points: Point[]
) => T;

// An algorithm that operates on a point
export type PointAlgorithm<T extends GeometricResult = GeometricResult> = (
  point: Point
) => T;

// An algorithm that operates on a 2d polygon
export type MultiPolygon2Algorithm<
  T extends GeometricResult = GeometricResult
> = (polygon: MultiPolygon2) => T;

// An algorithm that operates on a 2d point
export type Point2Algorithm<T extends GeometricResult = GeometricResult> = (
  point: Point2
) => T;

export type ParameterizedMultiPolygon2Algorithm<
  T extends GeometricResult = GeometricResult
> = (
  polygon: MultiPolygon2,
  buffer: number,
  externalShapes: SheetShapeDeepFragment[],
  unitScale: number
) => T;

export type CornerMultiPolygon2Algorithm<
  T extends GeometricResult = GeometricResult
> = (
  polygon: MultiPolygon2,
  roofHeight: number | undefined,
  unitScale: number
) => T;

export type RandMultiPolygon2Algorithm<
  T extends GeometricResult = GeometricResult
> = (
  polygon: MultiPolygon2,
  roofHeight: number | undefined,
  unitScale: number
) => T;

// An algorithm that operates on a 2d polyline
export type Polyline2Algorithm<T extends GeometricResult = GeometricResult> = (
  points: Point2[]
) => T;

export type SlopedInsulationAlgorithm<
  T extends GeometricResult = GeometricResult
> = (crossSection: SlopedInsulationCrossSection) => T;
